import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { parseISO, format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { SearchFormTreatments } from "../../components/organisms/search-form/treatments";
import { TherapistListingQueryKey } from "../../components/themed-v2/pages/therapist-listing-page/hooks/use-search-filter";
import {
  BookingLocationPresenter,
  inflateBookingLocationPresenter,
} from "../../presenters/booking-location";
import { ICategory } from "../../services/api/category-list";
import { IFeaturedTreatment } from "../../services/api/treatments";
import {
  actionCreators as categoriesActions,
  fetchCategoryList,
} from "../../store/categories/actions";
import { IRootState } from "../../store/index";
import {
  actionCreators as therapistsActions,
  fetchTherapistsSearch,
} from "../../store/therapists/actions";
import { dateFromIso } from "../../utils/dateIso";
import * as analytics from "../../analytics";
import { config } from "../../config";

export type SearchInputField = "Category" | "Address" | "Date" | "Time";

interface IState {
  isShowTooltip: boolean;
  isTooltipTextChanged: boolean;
  tooltipText: string;
}

interface IProps {
  category?: ICategory;
  categoryId?: string;
  isCategoryPreselected?: boolean;
  isHideDateTimeBooking?: boolean;
  dontClearSelectedCategoryFeaturedTreatment?: boolean;
  url?: string;
  onSelect?(): void;
  onSubmit?(): void;
}

interface IReduxProps {
  bookingLocation?: BookingLocationPresenter;
  isTherapistsLoading?: boolean;
  selectedCategory?: ICategory;
  selectedDate?: string;
  selectedCategoryFeaturedTreatment?: IFeaturedTreatment;
  categories?: ICategory[];
}

type RouteProps = RouteComponentProps<{ categoryName: string }>;
type Props = IProps & IReduxProps & RouteProps & { dispatch: Dispatch<any> };

class SearchFormTreatmentsContainer extends React.Component<Props, IState> {
  private readonly initialTooltipText =
    "Choose treatment, enter address then select date and time";

  public state: IState = {
    isShowTooltip: false,
    isTooltipTextChanged: false,
    tooltipText: this.initialTooltipText,
  };

  /**
   * Change tooltip text if category already selected
   */
  private setTooltipTextAndShow() {
    const {
      category,
      selectedCategory,
      selectedCategoryFeaturedTreatment,
      isCategoryPreselected,
    } = this.props;

    const isCategorySelected =
      category || selectedCategory || selectedCategoryFeaturedTreatment;

    if (
      (isCategorySelected || isCategoryPreselected) &&
      !this.state.isTooltipTextChanged
    ) {
      this.setState({
        isShowTooltip: true,
        isTooltipTextChanged: true,
        tooltipText: "Enter address then select date and time",
      });
    } else if (!isCategorySelected && this.state.isTooltipTextChanged) {
      this.setState({
        isShowTooltip: true,
        isTooltipTextChanged: false,
        tooltipText: this.initialTooltipText,
      });
    } else {
      this.setState({ isShowTooltip: true });
    }
  }

  public checkInputValidationOnClick = (justclose?: boolean) => {
    if (justclose && this.state.isShowTooltip) {
      this.setState({ isShowTooltip: false });
    } else if (!justclose) {
      this.setState({ isShowTooltip: true });
    }
  };

  public trackEvent = (
    field: SearchInputField,
    analyticsEvent: analytics.AnalyticsEvents,
    value?: string,
  ) => {
    if (this.props.url) {
      switch (analyticsEvent) {
        case analytics.AnalyticsEvents.SEARCH_WIDGET_FOCUSED:
          analytics.track({
            name: analyticsEvent,
            payload: {
              url: this.props.url,
              field,
            },
          });
          break;
        case analytics.AnalyticsEvents.SEARCH_WIDGET_CHANGED:
          analytics.track({
            name: analyticsEvent,
            payload: {
              url: this.props.url,
              field,
              ...(value && { value }),
            },
          });
          break;
      }
    }
  };

  public submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (this.props.isTherapistsLoading) {
      return;
    }

    // on search clear featured treatment by default
    if (
      !this.props.dontClearSelectedCategoryFeaturedTreatment &&
      this.props.selectedCategoryFeaturedTreatment
    ) {
      // tslint:disable-next-line: await-promise
      await this.props.dispatch(
        categoriesActions.clearCategoryFeaturedTreatment(),
      );
    }

    if (this.props.onSubmit) {
      this.props.onSubmit();
    }

    if (
      (this.props.selectedCategory || this.props.isCategoryPreselected) &&
      this.props.selectedDate &&
      this.props.bookingLocation &&
      dateFromIso(this.props.selectedDate).hour !== 0
    ) {
      this.props.dispatch(therapistsActions.clearTherapists());
      this.props.dispatch(therapistsActions.clearAdditionalTherapists());
      this.props.dispatch(therapistsActions.deSelectTherapist());
      this.props.dispatch(
        fetchTherapistsSearch({ availableAt: this.props.selectedDate }),
      );

      const selectedCategory = this.props.selectedCategory
        ? this.props.selectedCategory.code.toLowerCase()
        : "featured";
      const selectedDay = format(
        utcToZonedTime(
          parseISO(this.props.selectedDate),
          config.dateTime.timezone,
        ),
        "yyyy-MM-dd",
      );
      const selectedPostcode =
        this.props.bookingLocation.postcodeOutwardCode ||
        this.props.bookingLocation.postcode ||
        "";
      const params = new URLSearchParams({
        [TherapistListingQueryKey.CATEGORY]: selectedCategory,
        [TherapistListingQueryKey.POSTCODE]: selectedPostcode,
        [TherapistListingQueryKey.DATE]: selectedDay,
        [TherapistListingQueryKey.TIME]: "all",
      });

      this.props.history.push(`/book?${params.toString()}`);

      analytics.track({
        name: analytics.AnalyticsEvents.SEARCH_WIDGET_SUBMIT,
        payload: {
          category: selectedCategory,
          address: selectedPostcode,
          date: this.props.selectedDate,
        },
      });
    } else {
      this.setTooltipTextAndShow();
    }
  };

  public componentDidMount() {
    this.props.dispatch(fetchCategoryList());

    if (this.props.category) {
      this.props.dispatch(categoriesActions.setCategory(this.props.category));
    } else if (this.props.categoryId) {
      const category = this.props.categories?.find(
        (c) => c.code === this.props.categoryId.toUpperCase(),
      );

      if (category) {
        this.props.dispatch(categoriesActions.setCategory(category));
      }
    }
  }

  /**
   * Render treatments form
   */
  public render() {
    return (
      <SearchFormTreatments
        category={this.props.category || this.props.selectedCategory}
        isCategoryPreselected={this.props.isCategoryPreselected}
        isHideDateTimeBooking={this.props.isHideDateTimeBooking}
        isShowTooltip={this.state.isShowTooltip}
        isTherapistsLoading={this.props.isTherapistsLoading}
        onInputClick={this.checkInputValidationOnClick}
        onSelect={this.props.onSelect}
        onSubmit={this.submitForm}
        tooltipText={this.state.tooltipText}
        trackEvent={this.trackEvent}
      />
    );
  }
}

// store
const mapStateToProps = (state: IRootState): IReduxProps => {
  return {
    ...(state.postcodesState.bookingLocation && {
      bookingLocation: inflateBookingLocationPresenter(
        state.postcodesState.bookingLocation,
      ),
    }),
    isTherapistsLoading: state.therapistsState.isTherapistsLoading,
    selectedCategory: state.categoriesState.selectedCategory,
    selectedCategoryFeaturedTreatment:
      state.categoriesState.selectedCategoryFeaturedTreatment,
    selectedDate: state.therapistsState.selectedDate,
    categories: state.categoriesState.categories,
  };
};

const store = withRouter(
  connect<IReduxProps, {}, {}, IRootState>(mapStateToProps)(
    SearchFormTreatmentsContainer,
  ),
);

export { store as SearchFormTreatmentsContainer };
